import throttle from 'lodash/throttle';
import React, { useEffect, useRef, useState } from 'react';

import { getLayoutWidth } from './layoutWidth';

/**
 * A higher order component (HOC) that provides the current viewport
 * dimensions to the wrapped component as a `viewport` prop that has
 * the shape `{ width: 600, height: 400}`.
 * @param {React.ComponentType<any>} Component component to be wrapped by this HOC
 * @returns {React.ComponentType<any>} HOC component which knows its viewport dimensions
 */
export const withViewport = (Component) => {
  const WithViewportComponent = (props) => {
    const viewport = useViewport();
    return <Component {...props} viewport={viewport} />;
  };

  WithViewportComponent.displayName = `withViewport(${Component.displayName || Component.name})`;

  return WithViewportComponent;
};

/**
 * A higher order component (HOC) that provides dimensions to the wrapped component as a
 * `dimensions` prop that has the shape `{ width: 600, height: 400}`.
 *
 * @param {React.Component} Component to be wrapped by this HOC
 * @param {object} options pass in options like maxWidth and maxHeight.
 *
 * @returns {object} HOC component which knows its dimensions
 */
export const withDimensions = (Component, options = {}) => {
  // The resize event is flooded when the browser is resized. We'll
  // use a small timeout to throttle changing the viewport since it
  // will trigger rerendering.
  const THROTTLE_WAIT_MS = 200;
  // First render default wait after mounting (small wait for styled paint)
  const RENDER_WAIT_MS = 100;

  const WithDimensionsComponent = (props) => {
    const elementRef = useRef(null);
    const [dimensions, setDimensions] = useState({ width: 0, height: 0 });

    useEffect(() => {
      const setDimensionsFromElement = () => {
        const { clientWidth, clientHeight } = elementRef.current || {
          clientWidth: 0,
          clientHeight: 0,
        };
        setDimensions({ width: clientWidth, height: clientHeight });
      };

      const handleWindowResize = throttle(() => {
        window.requestAnimationFrame(setDimensionsFromElement);
      }, THROTTLE_WAIT_MS);

      window.addEventListener('resize', handleWindowResize);
      window.addEventListener('orientationchange', handleWindowResize);

      const defaultRenderTimeout = window.setTimeout(setDimensionsFromElement, RENDER_WAIT_MS);

      return () => {
        window.removeEventListener('resize', handleWindowResize);
        window.removeEventListener('orientationchange', handleWindowResize);
        window.clearTimeout(defaultRenderTimeout);
        handleWindowResize.cancel();
      };
    }, []);

    // Dimensions from state (i.e. dimension after previous resize)
    // These are needed for component rerenders
    const { width, height } = dimensions;

    // Current dimensions from element reference
    const { clientWidth, clientHeight } = elementRef.current || {
      clientWidth: 0,
      clientHeight: 0,
    };
    const hasDimensions =
      (width !== 0 && height !== 0) || (clientWidth !== 0 && clientHeight !== 0);

    // clientWidth and clientHeight
    const currentDimensions =
      clientWidth !== 0 && clientHeight !== 0
        ? { width: clientWidth, height: clientHeight }
        : width !== 0 && height !== 0
        ? { width, height }
        : {};

    // lazyLoadWithDimensions HOC needs to take all given space
    // unless max dimensions are provided through options.
    const { maxWidth, maxHeight } = options;
    const maxWidthMaybe = maxWidth ? { maxWidth } : {};
    const maxHeightMaybe = maxHeight ? { maxHeight } : {};
    const style =
      maxWidth || maxHeight
        ? { width: '100%', height: '100%', ...maxWidthMaybe, ...maxHeightMaybe }
        : { position: 'absolute', top: 0, right: 0, bottom: 0, left: 0 };

    return (
      <div ref={elementRef} style={style}>
        {hasDimensions ? <Component {...props} dimensions={currentDimensions} /> : null}
      </div>
    );
  };

  WithDimensionsComponent.displayName = `withDimensions(${
    Component.displayName || Component.name
  })`;

  return WithDimensionsComponent;
};

/**
 * A higher order component (HOC) that lazy loads the current element and provides
 * dimensions to the wrapped component as a `dimensions` prop that has
 * the shape `{ width: 600, height: 400}`.
 *
 * @param {React.Component} Component to be wrapped by this HOC
 * @param {object} options pass in options like maxWidth and maxHeight. To load component after
 * initial rendering has passed or after user has interacted with the window (e.g. scrolled),
 * use`loadAfterInitialRendering: 1500` (value should be milliseconds).
 *
 * @returns {object} HOC component which knows its dimensions
 */
export const lazyLoadWithDimensions = (Component, options = {}) => {
  // The resize event is flooded when the browser is resized. We'll
  // use a small timeout to throttle changing the viewport since it
  // will trigger rerendering.
  const THROTTLE_WAIT_MS = 200;
  // First render default wait after mounting (small wait for styled paint)
  const RENDER_WAIT_MS = 100;

  // Scrolling and other events that affect to viewport location have this safety margin
  // for lazy loading
  const NEAR_VIEWPORT_MARGIN = 50;

  const LazyLoadWithDimensionsComponent = (props) => {
    const { width: initialWidth, height: initialHeight, alignment } = props;
    const elementRef = useRef(null);
    const [dimensions, setDimensions] = useState(() => ({
      width: initialWidth || 0,
      height: initialHeight || 0,
    }));

    useEffect(() => {
      const setDimensionsFromElement = () => {
        const { clientWidth, clientHeight } = elementRef.current || {
          clientWidth: 0,
          clientHeight: 0,
        };
        if (options.mode === 'auto') {
          setDimensions({ width: clientWidth, height: 'auto' });
        } else {
          setDimensions({ width: clientWidth, height: clientHeight });
        }
      };

      const isElementNearViewport = (safetyBoundary) => {
        if (elementRef.current) {
          const rect = elementRef.current.getBoundingClientRect();
          const viewportHeight = window.innerHeight || document.documentElement.clientHeight;

          return (
            (rect.top >= 0 && rect.top <= viewportHeight + safetyBoundary) ||
            (rect.bottom >= -1 * safetyBoundary && rect.bottom <= viewportHeight)
          );
        }
        return false;
      };

      const handleWindowResize = throttle(() => {
        const shouldLoadToImproveScrolling = typeof options.loadAfterInitialRendering === 'number';
        if (isElementNearViewport(NEAR_VIEWPORT_MARGIN) || shouldLoadToImproveScrolling) {
          window.requestAnimationFrame(setDimensionsFromElement);
        }
      }, THROTTLE_WAIT_MS);

      window.addEventListener('scroll', handleWindowResize);
      window.addEventListener('resize', handleWindowResize);
      window.addEventListener('orientationchange', handleWindowResize);

      let afterRenderTimeout = null;
      const defaultRenderTimeout = window.setTimeout(() => {
        if (isElementNearViewport(0)) {
          setDimensionsFromElement();
        } else {
          const loadAfterInitialRendering = options.loadAfterInitialRendering;
          if (typeof loadAfterInitialRendering === 'number') {
            afterRenderTimeout = window.setTimeout(() => {
              window.requestAnimationFrame(setDimensionsFromElement);
            }, loadAfterInitialRendering);
          }
        }
      }, RENDER_WAIT_MS);

      return () => {
        window.removeEventListener('scroll', handleWindowResize);
        window.removeEventListener('resize', handleWindowResize);
        window.removeEventListener('orientationchange', handleWindowResize);
        window.clearTimeout(defaultRenderTimeout);

        if (afterRenderTimeout) {
          window.clearTimeout(afterRenderTimeout);
        }
        handleWindowResize.cancel();
      };
    }, []);

    const { width, height } = dimensions;

    // lazyLoadWithDimensions HOC needs to take all given space
    // unless max dimensions are provided through options.
    const { maxWidth, maxHeight, mode } = options;
    const maxWidthMaybe = maxWidth ? { maxWidth } : {};
    const maxHeightMaybe = maxHeight ? { maxHeight } : {};
    const justifyContent =
      alignment === 'left'
        ? 'flex-start'
        : alignment === 'center'
        ? 'center'
        : alignment === 'right'
        ? 'flex-end'
        : 'flex-start';
    const style = mode
      ? {
          display: 'flex',
          width: 'auto',
          height: 'auto',
          justifyContent: justifyContent,
        }
      : maxWidth || maxHeight
      ? {
          width: '100%',
          height: '100%',
          ...maxWidthMaybe,
          ...maxHeightMaybe,
        }
      : { position: 'absolute', top: 0, right: 0, bottom: 0, left: 0 };

    if (mode === 'auto') {
      return (
        <div ref={elementRef} style={style}>
          <Component {...props} dimensions={dimensions} />
        </div>
      );
    }
    return (
      <div ref={elementRef} style={style}>
        {(width !== 0 && height !== 0) || (width === 'auto' && height === 'auto') ? (
          <Component {...props} dimensions={dimensions} />
        ) : null}
      </div>
    );
  };

  LazyLoadWithDimensionsComponent.displayName = `lazyLoadWithDimensions(${
    Component.displayName || Component.name
  })`;

  return LazyLoadWithDimensionsComponent;
};

/**
 * A hook that provides the current viewport dimensions
 * @returns {{width: number, height: number}} The current viewport dimensions
 */
export const useViewport = () => {
  const [viewport, setViewport] = useState({ width: 0, height: 0 });

  useEffect(() => {
    const handleResize = throttle(() => {
      setViewport({
        width: getLayoutWidth(),
        height: window.innerHeight,
      });
    }, 100);

    handleResize();
    window.addEventListener('resize', handleResize);
    window.addEventListener('orientationchange', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('orientationchange', handleResize);
    };
  }, []);

  return viewport;
};
